import { useEffect, useRef } from "react"
import { View, Pressable, TextInput as RNTextInput } from "react-native"
import MCIcon from "@react-native-vector-icons/material-design-icons"
import { Link } from "expo-router"
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Image } from "expo-image"
import { Show, useObservable } from "@legendapp/state/react"

import { trpc } from "utils/trpc"
import { priceFromDBFormat } from "@src/utils"
import { UpcInput } from "../upc-input"
import { productFormSchema, TransactionFormControl } from "@white-labels/input/src/m5azen"
import { z } from "@white-labels/input"
import { ar } from "i18n/ar"
import { Button, Text, TextInput, ModalWrapper } from "components/core"
import { PriceInput } from "../price-input"
import { ENV } from "@src/utils/env"
import { arabicToEnglishNumbers } from "@white-labels/utils"

export function ProductTransactionForm(props: { control: TransactionFormControl }) {
  const { fields, append, update, remove } = useFieldArray({ control: props.control, name: "products" })
  const editModal$ = useObservable<string | undefined>(undefined)

  // Refs for keyboard flow
  const upcInputRef = useRef<{ focus: () => void }>(null)
  const costInputRef = useRef<RNTextInput>(null)
  const priceInputRef = useRef<RNTextInput>(null)

  const products = trpc.useQueries((t) => {
    return fields.map((field) => t.products.getByUpc({ upc: field.sku }))
  })

  const { control, setValue, formState, handleSubmit, reset } = useForm({
    defaultValues: { sku: __DEV__ ? "6223004796272" : "" }, // 5999884034469
    resolver: zodResolver(productFormSchema),
  })

  const sku = useWatch({ control, name: "sku" })
  const enabled = (sku?.trim().length ?? 0) >= 3
  const product = trpc.products.getByUpc.useQuery({ upc: sku?.trim() as string }, { enabled, retry: 0 })

  const { isValid } = formState
  const disabled = !isValid || product.status !== "success"

  // take a look at this.
  useEffect(() => {
    if ((product.data?.upc?.length ?? 0) >= 3 && product.status === "success") {
      setValue("cost", product.data.cost ?? 0)
      setValue("suggestedPrice", product.data.price)
    }
  }, [product.data])

  const handleConfirm = handleSubmit((data) => {
    const index = fields.findIndex((field) => field.sku === data.sku)
    if (index !== -1) {
      const existingProduct = fields[index]
      update(index, {
        ...existingProduct,
        cost: data.cost,
        suggestedPrice: data.suggestedPrice,
        quantity: (existingProduct.quantity ?? 0) + (data.quantity ?? 0),
      })
    } else {
      append(data)
    }
    requestAnimationFrame(() => {
      reset()
      // Auto-focus UPC input after adding product
      upcInputRef.current?.focus()
    })
  })

  return (
    <>
      <View className="flex-row justify-between items-center mb-2">
        <Text className="text-lg font-bold text-gray-800">{ar.products}</Text>
        <Link href="/dashboard/products/create" asChild>
          <Pressable className="px-2 active:opacity-50" hitSlop={12}>
            <MCIcon name="plus" size={28} color="#4f46e5" />
          </Pressable>
        </Link>
      </View>

      {fields.map((field, index) => {
        const product = products[index]
        const image = product.data?.images.at(0)?.key

        const totalCost = (field.cost || 0) * field.quantity

        return (
          <View className="border border-gray-200 rounded p-1 mb-2 active:opacity-70 bg-gray-50" key={field.sku}>
            <View className="flex-row justify-between items-start mb-2">
              <View className="flex-row items-center flex-1">
                {image && (
                  <Image source={{ uri: `${ENV.R2_URL}/${image}` }} className="w-12 h-12 rounded mr-3" contentFit="contain" transition={250} loading="lazy" cachePolicy="memory" />
                )}
                <Link href={`/dashboard/products/${product.data?.id}`} asChild>
                  <Pressable className="flex-1">
                    <Text className="font-semibold text-gray-800 text-sm" numberOfLines={1}>
                      {product.data?.name}
                    </Text>
                    <Text className="text-gray-500 text-xs mt-1">{field.sku}</Text>
                  </Pressable>
                </Link>
              </View>

              <View className="flex-row gap-x-1">
                <Pressable className="p-2 active:opacity-50" onPress={() => editModal$.set(field.sku)}>
                  <MCIcon name="pencil" size={20} color="blue" />
                </Pressable>

                <Pressable
                  onPress={(e) => {
                    e.stopPropagation()
                    remove(index)
                  }}
                  className="p-2 active:opacity-50"
                >
                  <MCIcon name="delete" size={20} color="#ef4444" />
                </Pressable>
              </View>
            </View>

            <View className="flex-row justify-between gap-2">
              <View className="flex-1 items-center bg-white rounded py-1 border border-gray-200">
                <Text className="text-gray-600 text-xs mb-1/2">{ar.qty}</Text>
                <Text className="text-gray-800 text-sm font-semibold">{field.quantity}</Text>
              </View>
              <View className="flex-1 items-center bg-white rounded py-1 border border-gray-200">
                <Text className="text-gray-600 text-xs mb-1/2">{ar.cost}</Text>
                <Text className="text-gray-800 text-sm font-semibold">{priceFromDBFormat(field.cost || 0).toFixed(2)}</Text>
              </View>
              <View className="flex-1 items-center bg-white rounded py-1 border border-gray-200">
                <Text className="text-gray-600 text-xs mb-1/2">{ar.price}</Text>
                <Text className="text-gray-800 text-sm font-semibold">{priceFromDBFormat(field.suggestedPrice || 0).toFixed(2)}</Text>
              </View>
              <View className="flex-1 items-center bg-blue-50 rounded py-1 border border-blue-200">
                <Text className="text-gray-600 text-xs mb-1/2">{ar.totalCost}</Text>
                <Text className="text-blue-600 text-sm font-bold">{priceFromDBFormat(totalCost).toFixed(2)}</Text>
              </View>
            </View>
          </View>
        )
      })}
      <View className="mb-4 pb-4 border-t border-gray-200 pt-4">
        <Controller
          control={control}
          name="sku"
          render={({ field }) => {
            return (
              <UpcInput
                ref={upcInputRef} //
                value={field.value}
                onValueChange={field.onChange}
                withSearch
                // skipFetching
              />
            )
          }}
        />

        {product.error?.data?.code === "NOT_FOUND" ? (
          <Link href={`/dashboard/products/create?upc=${sku}`} asChild>
            <Pressable className="p-3 mb-4 active:opacity-70 bg-blue-50 rounded border border-blue-200">
              <Text className="text-center text-blue-600 font-bold">{ar.productNotFoundTapToAdd}</Text>
            </Pressable>
          </Link>
        ) : null}

        {product.data?.upc && (
          <Link href={`/dashboard/products/${product.data.id}`} asChild>
            <Pressable className="active:opacity-70 mb-3">
              <Text className="text-blue-500 py-2 px-1 text-sm">{product.data.name}</Text>
            </Pressable>
          </Link>
        )}

        <View className="flex-row mb-4 gap-1">
          <View className="flex-1">
            <Text className="text-sm text-gray-700 mb-2">{ar.qty}</Text>
            <Controller
              control={control}
              name="quantity"
              render={({ field }) => (
                <TextInput
                  value={field.value?.toString() ?? ""}
                  selectTextOnFocus
                  onChangeText={(value) => {
                    const text = arabicToEnglishNumbers(value)
                    if (z.safeParse(z.coerce.number(), text).success) {
                      field.onChange(text)
                    }
                  }}
                  placeholder="0"
                  keyboardType="numeric"
                  returnKeyType="next"
                  onSubmitEditing={() => costInputRef.current?.focus()}
                />
              )}
            />
          </View>
          <View className="flex-1">
            <Text className="text-sm text-gray-700 mb-2">{ar.cost}</Text>
            <Controller
              control={control}
              name="cost"
              render={({ field }) => (
                <PriceInput
                  ref={costInputRef}
                  value={field.value} //
                  onValueChange={field.onChange}
                  returnKeyType="next"
                  onSubmitEditing={() => priceInputRef.current?.focus()}
                />
              )}
            />
          </View>
          <View className="flex-1">
            <Text className="text-sm text-gray-700 mb-2">{ar.price}</Text>
            <Controller
              control={control}
              name="suggestedPrice"
              render={({ field }) => (
                <PriceInput
                  ref={priceInputRef}
                  value={field.value} //
                  onValueChange={field.onChange}
                  returnKeyType="done"
                  onSubmitEditing={handleConfirm}
                />
              )}
            />
          </View>
        </View>
        <Button disabled={disabled} onPress={handleConfirm}>
          {ar.addProduct}
        </Button>
      </View>

      <Show if={() => editModal$.get() !== undefined}>
        {() => {
          const upc = editModal$.get()
          const index = fields.findIndex((field) => field.sku === upc)
          const product = fields[index]
          return (
            <EditProductModal
              product={product}
              onSave={(updatedData) => {
                update(index, updatedData)
                editModal$.set(undefined)
              }}
              hide={() => editModal$.set(undefined)}
            />
          )
        }}
      </Show>
    </>
  )
}

type EditProductModalProduct = { cost: number; suggestedPrice: number; quantity: number; sku: string }
type EditProductModal = {
  product: EditProductModalProduct
  onSave: (data: EditProductModalProduct) => void
  hide: () => void
}

function EditProductModal({ product, onSave, hide }: EditProductModal) {
  const { control, handleSubmit, formState } = useForm({
    defaultValues: { sku: product?.sku, quantity: product?.quantity, cost: product?.cost, suggestedPrice: product?.suggestedPrice },
    resolver: zodResolver(productFormSchema),
  })
  const productData = trpc.products.getByUpc.useQuery(
    { upc: product.sku }, //
    { enabled: !!product.sku, retry: 0 },
  )

  const handleSave = handleSubmit(onSave)

  return (
    <ModalWrapper onClose={hide}>
      <View className="flex-row justify-between items-center mb-2">
        <Text className="text-xl font-bold text-gray-800">{ar.edit}</Text>
        <Pressable onPress={hide} className="p-2 active:opacity-50">
          <MCIcon name="close" size={24} color="#374151" />
        </Pressable>
      </View>

      {product.sku && (
        <View className="flex-row items-center gap-1 p-2 bg-gray-50 rounded border border-gray-200">
          {productData.data?.images?.at(0)?.key && (
            <Image
              source={{ uri: `${ENV.R2_URL}/${productData.data.images.at(0)?.key}` }}
              className="w-16 h-16 rounded"
              contentFit="contain"
              transition={250}
              loading="lazy"
              cachePolicy="memory"
            />
          )}
          <View className="flex-1">
            <Text className="font-semibold text-gray-800">{productData.data?.name}</Text>
            <Text className="text-gray-500 text-sm mt-1">{product.sku}</Text>
          </View>
        </View>
      )}

      <View className="flex-row gap-1 mb-2">
        <View className="flex-1">
          <Text className="text-sm text-gray-700 mb-2">{ar.qty}</Text>
          <Controller
            control={control}
            name="quantity"
            render={({ field }) => (
              <TextInput
                selectTextOnFocus
                value={field.value?.toString() ?? ""}
                onChangeText={(value) => {
                  if (z.safeParse(z.coerce.number(), value).success) {
                    field.onChange(value)
                  }
                }}
                placeholder="0"
                keyboardType="numeric"
              />
            )}
          />
        </View>

        <View className="flex-1">
          <Text className="text-sm text-gray-700 mb-2">{ar.cost}</Text>
          <Controller
            control={control}
            name="cost"
            render={({ field }) => <PriceInput selectTextOnFocus value={field.value ? Number(field.value) : undefined} onValueChange={field.onChange} />}
          />
        </View>

        <View className="flex-1">
          <Text className="text-sm text-gray-700 mb-2">{ar.price}</Text>
          <Controller
            control={control}
            name="suggestedPrice"
            render={({ field }) => <PriceInput selectTextOnFocus value={field.value ? Number(field.value) : undefined} onValueChange={field.onChange} />}
          />
        </View>
      </View>

      <Button disabled={!formState.isValid} onPress={handleSave}>
        {ar.save}
      </Button>
    </ModalWrapper>
  )
}
